/**
 * @file upgrade-v3.ts
 * @description Подъём payload секции v2 (ступени) до v3 (числа) — phase19.16,
 * партия SB120 «свобода выбора», этап 1 «секция-герой» (site-builder.md
 * SB120, SB100; проект данных
 * docs/phases/phase19.16-materials/db-design-sb120-stage1.md §2, проект UI
 * uiux-sb120-stage1.md §5 и «Дополнение» §5).
 *
 * Принцип (uiux-sb120-fields §6): после перевода ни один пиксель на сайте не
 * сдвигается. Явные ступени становятся px по плотности магазина на момент
 * перевода (`spaceStepPx`, legacy-steps.ts), «как у сайта» остаётся
 * наследуемым (нет ключа). Поэтому плотность — вход подъёма: её дают сервер
 * (плотность магазина, своей версии сайта) и импорт пакета «Как было».
 * Нет плотности, а явная ступень есть — «Обычно» и заметка в `notes`.
 *
 * Таблица перевода (§2; d — плотность):
 *   отступы «site»            → нет ключа (inner → нет innerTop и innerBottom);
 *   отступы-ступени           → spaceStepPx(ступень, d); inner → innerTop = innerBottom;
 *   phoneTighter: true        → space.phoneFactor 0,3 (S/M/L и «site» — точно:
 *                               ступень ниже × 0,6 = ПК × 0,3); xl → своё
 *                               phone.<поле> = L × 0,6, xs → phone.<поле> = 0;
 *   phoneTighter: false       → нет ключа (доля темы 0,6);
 *   overlap, bg.bleed none/s/m → 0 / spaceStepPx(L, d) / spaceStepPx(XL, d);
 *   gap, elementGap s/l       → spaceStepPx(S|L, d); m → нет ключа (уточнение:
 *                               «M сайта» = 32 × d — те же пиксели при любой
 *                               плотности, v2 «m» тоже шёл за плотностью);
 *   ratio                     → shares (SECTION_RATIO_SHARES; не совпало с
 *                               числом колонок — равные доли);
 *   phoneOrder reverse        → [2, 1] (только у двух колонок, как в v2);
 *   container full/grid/narrow → "full" / нет ключа / 768;
 *   card bool                 → { on };
 *   height auto / screen      → { content } / { screen 100 } + heightPhone { content };
 *   items[].bg роль           → { kind: "role", role };
 *   decor anchor / size       → x, y из точки 3×3 / px ПК и телефона (DECOR_SIZE_PX);
 *                               негодные точка или размер — рисунок снят (как в v2);
 *   header.line bool          → { width: 1 | 0 };
 *   фото fill / fit / bleed   → fit + высота «По колонке» / fit + «По фото» / bleed + «По фото».
 * Функция тотальная: неизвестное значение — ключ не пишется (дальше
 * умолчания и починка `readSectionPayload`). Перед переводом недостающие
 * ключи v2 берутся из умолчаний v2 — иначе отсутствующий `phoneTighter`
 * (в v2 умолчание true) перевёлся бы как false и сдвинул телефон.
 * Вход не меняется.
 */

import type { SiteDensityInput } from "../site-theme/legacy-v1.js";
import { mergeMissingKeys } from "../site-theme/schema.js";
import { cloneJson } from "../site-theme/repair.js";
import {
  anchorToPoint,
  CONTAINER_V3,
  DECOR_SIZE_PX,
  GAP_STEP,
  OVERLAP_STEP,
  spaceStepPx,
} from "../site-theme/legacy-steps.js";
import {
  equalShares,
  ratioColumnCount,
  SECTION_BG_ROLES,
  SECTION_RATIO_SHARES,
  SECTION_RATIOS,
  SPACE_STEPS,
  type SectionRatio,
  type SpaceStep,
} from "./schema.js";

/** Что нужно подъёму v2 → v3. */
export interface SectionUpgradeV2Options {
  /** Плотность отступов магазина (той версии сайта, которой принадлежит блок). */
  density?: SiteDensityInput;
}

/** Доля телефона у «На телефоне плотнее» v2: ступень ниже × 0,6 = ПК × 0,3. */
export const PHONE_TIGHTER_FACTOR = 0.3;

/** Доля телефона темы в v2 (globals.css `--site-space-mobile`). */
const V2_PHONE_FACTOR = 0.6;

/** Заметка «плотности не было» — одна на секцию. */
export const SECTION_DENSITY_NOTE = "Плотность магазина не передана — ступени отступов переведены в px по «Обычно»";

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

/** Убрать хвосты плавающей точки (51,2 × 0,6 = 30,720000000000002), не округляя до 0,1. */
const tidy = (value: number): number => Math.round(value * 10_000) / 10_000;

// ── Умолчания v2 (как defaults.ts до v3) — для слияния перед переводом ──

/** Оформление v2 по умолчанию: прозрачный фон, по сетке, «как у сайта», плотнее на телефоне. */
export function defaultSectionLookV2(): Record<string, unknown> {
  return {
    bg: { kind: "transparent" },
    container: "grid",
    card: false,
    height: "auto",
    decor: [],
    space: {
      outerTop: "site",
      outerBottom: "site",
      inner: "site",
      overlap: "none",
      mergeWithPrev: false,
      phoneTighter: true,
    },
    visibility: { phone: true, tablet: true, desktop: true },
  };
}

/** Настройки колонок v2 по умолчанию (без списка колонок). */
const V2_COLUMNS_DEFAULTS = { ratio: "1", gap: "m", valign: "center", elementGap: "m", phoneOrder: "as-is" };
const V2_MEDIA_BG_DEFAULTS = {
  focus: { x: 50, y: 50 },
  fade: "none",
  dim: 0,
  textTone: "light",
  bleed: { top: "none", bottom: "none", side: "none" },
};
const V2_DECOR_DEFAULTS = { size: "m", hideOnPhone: true, bleed: false, column: null };
const V2_HEADER_DEFAULTS = { level: 2, line: false };

// ── Перевод ──

interface Ctx {
  density: SiteDensityInput;
  /** Встретилась явная ступень, которой нужна плотность. */
  usedDensity: boolean;
  notes: string[];
}

const isSpaceStep = (value: unknown): value is SpaceStep =>
  typeof value === "string" && (SPACE_STEPS as readonly string[]).includes(value);
const isRatio = (value: unknown): value is SectionRatio =>
  typeof value === "string" && (SECTION_RATIOS as readonly string[]).includes(value);
const hasKey = <K extends string>(table: Readonly<Record<K, unknown>>, value: unknown): value is K =>
  typeof value === "string" && Object.prototype.hasOwnProperty.call(table, value);

/** Ступень → px при плотности магазина (плотность «нужна», если ступень не 0). */
function stepPx(step: SpaceStep, ctx: Ctx): number {
  if (step !== "0") ctx.usedDensity = true;
  return spaceStepPx(step, ctx.density);
}

/** Наезд и выход за край: none/s/m → px; неизвестное — undefined. */
function overlapPx(value: unknown, ctx: Ctx): number | undefined {
  return hasKey(OVERLAP_STEP, value) ? stepPx(OVERLAP_STEP[value], ctx) : undefined;
}

/** Расстояние колонок: m — «как у сайта» (нет ключа), s/l — px; неизвестное — нет ключа. */
function gapPx(value: unknown, ctx: Ctx): number | undefined {
  if (!hasKey(GAP_STEP, value) || value === "m") return undefined;
  return stepPx(GAP_STEP[value], ctx);
}

/** Отступы секции v2 → v3. */
function spaceV3(raw: unknown, ctx: Ctx): unknown {
  if (!isPlainObject(raw)) return raw;
  const tighter = typeof raw["phoneTighter"] === "boolean" ? raw["phoneTighter"] : true;
  const out: Record<string, unknown> = {};
  const phone: Record<string, number> = {};
  const put = (fields: readonly string[], value: unknown): void => {
    // «site» и неизвестное — наследуется (ключа нет).
    if (!isSpaceStep(value)) return;
    const px = stepPx(value, ctx);
    for (const field of fields) {
      out[field] = px;
      // «Плотнее»: витрина v2 опускала ступень на одну и умножала на 0,6.
      // Для XL ступень ниже — L (не половина), для XS — 0: своё число телефона.
      if (tighter && value === "xl") phone[field] = tidy(spaceStepPx("l", ctx.density) * V2_PHONE_FACTOR);
      if (tighter && value === "xs") phone[field] = 0;
    }
  };
  put(["outerTop"], raw["outerTop"]);
  put(["outerBottom"], raw["outerBottom"]);
  put(["innerTop", "innerBottom"], raw["inner"]);
  if (Object.keys(phone).length > 0) out["phone"] = phone;
  if (tighter) out["phoneFactor"] = PHONE_TIGHTER_FACTOR;
  out["overlap"] = overlapPx(raw["overlap"], ctx) ?? 0;
  out["mergeWithPrev"] = raw["mergeWithPrev"] === true;
  return out;
}

/** Фон v2 → v3: у фото — выход за край в px; остальные варианты без изменений. */
function bgV3(raw: unknown, ctx: Ctx): unknown {
  if (!isPlainObject(raw) || raw["kind"] !== "media") return raw;
  const merged = mergeMissingKeys(V2_MEDIA_BG_DEFAULTS, raw) as Record<string, unknown>;
  const bleed = merged["bleed"];
  if (isPlainObject(bleed)) {
    const next: Record<string, unknown> = { ...bleed };
    for (const side of ["top", "bottom"] as const) {
      const px = overlapPx(bleed[side], ctx);
      if (px === undefined) delete next[side];
      else next[side] = px;
    }
    merged["bleed"] = next;
  }
  return merged;
}

/**
 * Рисунок v2 → v3: точка 3×3 → x/y %, размер S/M/L → px ПК и телефона.
 * Негодная точка (нет, не 1–9) или размер (не S/M/L) — null: в v2 такой
 * рисунок не проходил проверку и снимался при чтении, после подъёма он не
 * должен появиться на сайте (по центру, 160 px из умолчаний v3).
 */
function decorV3(raw: unknown): unknown {
  if (!isPlainObject(raw)) return raw;
  const { anchor, size, ...rest } = mergeMissingKeys(V2_DECOR_DEFAULTS, raw) as Record<string, unknown>;
  const validAnchor = typeof anchor === "number" && Number.isInteger(anchor) && anchor >= 1 && anchor <= 9;
  if (!validAnchor || !hasKey(DECOR_SIZE_PX, size)) return null;
  return {
    ...rest,
    ...anchorToPoint(anchor),
    size: DECOR_SIZE_PX[size].desktop,
    sizePhone: DECOR_SIZE_PX[size].phone,
  };
}

/** Рисунки v2 → v3; негодные снимаются с заметкой (как снимало чтение v2). */
function decorListV3(raw: unknown, ctx: Ctx): unknown {
  if (!Array.isArray(raw)) return raw;
  const out: unknown[] = [];
  raw.forEach((item, index) => {
    const decor = decorV3(item);
    if (decor === null) ctx.notes.push(`Рисунок ${index + 1}: место или размер не из допустимых — снят`);
    else out.push(decor);
  });
  return out;
}

/** Оформление v2 → v3. */
function lookV3(raw: unknown, ctx: Ctx): unknown {
  if (!isPlainObject(raw)) return raw;
  const merged = mergeMissingKeys(defaultSectionLookV2(), raw) as Record<string, unknown>;
  const { container, card, height, bg, decor, space, ...rest } = merged;
  const out: Record<string, unknown> = { ...rest, bg: bgV3(bg, ctx) };
  if (hasKey(CONTAINER_V3, container) && CONTAINER_V3[container] !== undefined) out["container"] = CONTAINER_V3[container];
  out["card"] = { on: card === true };
  if (height === "screen") {
    // «Первый экран» v2: на ПК и планшете — окно без шапки, на телефоне по содержимому.
    out["height"] = { kind: "screen", value: 100 };
    out["heightPhone"] = { kind: "content" };
  } else {
    out["height"] = { kind: "content" };
  }
  out["decor"] = decorListV3(decor, ctx);
  out["space"] = spaceV3(space, ctx);
  return out;
}

/** Элемент: у фото режим и высота; остальные виды в этапе 1 не меняются. */
function elementV3(raw: unknown): unknown {
  if (!isPlainObject(raw) || raw["kind"] !== "image") return raw;
  const out: Record<string, unknown> = { ...raw };
  if (raw["mode"] === "fill") {
    out["mode"] = "fit";
    out["height"] = { kind: "column" };
  } else {
    // bleed остаётся, всё прочее (fit, нет ключа, неизвестное) — «обычное».
    out["mode"] = raw["mode"] === "bleed" ? "bleed" : "fit";
    out["height"] = { kind: "photo" };
  }
  return out;
}

/** Колонка: фон-роль → заливка; элементы. */
function columnV3(raw: unknown, index: number, ctx: Ctx): unknown {
  if (!isPlainObject(raw)) return raw;
  const { bg, elements, ...rest } = raw;
  const out: Record<string, unknown> = { ...rest };
  if (typeof bg === "string" && (SECTION_BG_ROLES as readonly string[]).includes(bg)) {
    out["bg"] = { kind: "role", role: bg };
  } else if (bg !== undefined) {
    ctx.notes.push(`Колонка ${index + 1}: фон не из ролей — снят`);
  }
  out["elements"] = Array.isArray(elements) ? elements.map(elementV3) : elements;
  return out;
}

/** Колонки v2 → v3. */
function columnsV3(raw: unknown, ctx: Ctx): unknown {
  if (!isPlainObject(raw)) return raw;
  const { ratio, gap, elementGap, phoneOrder, items, ...rest } = mergeMissingKeys(V2_COLUMNS_DEFAULTS, raw) as Record<
    string,
    unknown
  >;
  const count = Array.isArray(items) ? Math.min(4, Math.max(1, items.length)) : 1;
  const out: Record<string, unknown> = { ...rest };
  if (isRatio(ratio) && ratioColumnCount(ratio) === count) {
    out["shares"] = [...SECTION_RATIO_SHARES[ratio]];
  } else {
    out["shares"] = equalShares(count);
    ctx.notes.push("Пропорции колонок не совпадали с числом колонок — взяты равные доли");
  }
  const gapValue = gapPx(gap, ctx);
  if (gapValue !== undefined) out["gap"] = gapValue;
  const elementGapValue = gapPx(elementGap, ctx);
  if (elementGapValue !== undefined) out["elementGap"] = elementGapValue;
  if (phoneOrder === "reverse" && count === 2) out["phoneOrder"] = [2, 1];
  out["items"] = Array.isArray(items) ? items.map((col, index) => columnV3(col, index, ctx)) : items;
  return out;
}

/** Шапка: линия-флажок → толщина. */
function headerV3(raw: unknown): unknown {
  if (!isPlainObject(raw)) return raw;
  const merged = mergeMissingKeys(V2_HEADER_DEFAULTS, raw) as Record<string, unknown>;
  return { ...merged, line: { width: merged["line"] === true ? 1 : 0 } };
}

/**
 * v2 → v3 (запись реестра `SECTION_UPGRADES`, upgrade.ts). Результат
 * окончательно проверяют `readSectionPayload` / `validateSectionPayload`.
 */
export function upgradeV2(doc: unknown, opts: SectionUpgradeV2Options, notes: string[]): unknown {
  if (!isPlainObject(doc)) {
    notes.push("Payload v2 не объект — взята пустая секция");
    return {};
  }
  const ctx: Ctx = { density: opts.density ?? 1, usedDensity: false, notes: [] };
  const source = cloneJson(doc);
  const out: Record<string, unknown> = { ...source };
  if (source["header"] !== undefined) out["header"] = headerV3(source["header"]);
  out["columns"] = columnsV3(source["columns"] ?? {}, ctx);
  out["look"] = lookV3(source["look"] ?? {}, ctx);
  if (ctx.usedDensity && opts.density === undefined) notes.push(SECTION_DENSITY_NOTE);
  notes.push(...ctx.notes);
  return out;
}
